import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Drawer } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Dropdown, { DropdownOption } from 'v3/Components/Form/Dropdown';
import TextInput from 'v3/Components/Form/TextInput';
import TextArea from 'v3/Components/Form/TextArea';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import { useEnums, useToasts } from 'Context';
import { buildEndpoint, useDataPoster } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { ReviewerNote } from 'v3/Models/ReviewerNotes';
import './ReviewerProfile.NoteDrawer.scss';

const EMPTY_OPTION: DropdownOption = { value: '', displayName: '' };

type FormValues = {
  type: DropdownOption;
  title: string;
  comment: string;
};

const INITIAL_VALUES: FormValues = {
  type: EMPTY_OPTION,
  title: '',
  comment: '',
};

type Props = {
  reviewerId: string;
  open: boolean;
  note?: ReviewerNote | null;
  deleting?: boolean;
  canDelete?: boolean;
  canEditBody?: boolean;
  onClose: () => void;
  onSuccess: (note: ReviewerNote) => void;
  onDelete?: () => void;
};

export const NoteDrawer: React.FC<Props> = ({
  reviewerId,
  open,
  note,
  deleting = false,
  canDelete = false,
  canEditBody = false,
  onClose,
  onSuccess,
  onDelete,
}) => {
  const toast = useToasts();
  const { reviewerNoteType } = useEnums();
  const [saving, setSaving] = useState(false);
  // Snapshotted on open rather than derived live from `note`, so that when the parent clears
  // `note` on save/close, the drawer doesn't flip from edit to add mode mid-close-animation
  // (antd keeps the content mounted and animating for a beat after `open` goes false).
  const [isEdit, setIsEdit] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!open) return;
    setIsEdit(!!note);
    formik.resetForm({
      values: note
        ? {
            type: note.type ? { value: note.type.value, displayName: note.type.displayName } : EMPTY_OPTION,
            title: note.title,
            comment: note.comment ?? '',
          }
        : INITIAL_VALUES,
    });
  }, [open, note]);

  // On add, the comment is the required note body. On edit, it's only shown/editable with
  // EditBody — otherwise the first comment (the body) is left untouched by this drawer.
  const showComment = !isEdit || canEditBody;

  const [postAdd] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'notes'),
    undefined,
    false,
    '3.0'
  );

  const handleSave = async () => {
    const { type, title, comment } = formik.values;

    setSaving(true);
    try {
      const resp: any = await postAdd({
        id: note?.id ?? null,
        title: title.trim(),
        ...(showComment ? { comment: comment.trim() } : {}),
        type: type.value === '' || type.value == null
          ? null
          : { value: Number(type.value), displayName: type.displayName },
      });

      if (resp?.status === 200 || resp?.status === 201) {
        onSuccess(resp.data as ReviewerNote);
        toast.add(isEdit ? 'Note was updated successfully' : 'Note was added successfully', { type: 'success', position: 'bottom-left' });
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          formik.setErrors(mappedErrors);
        }
        toast.add('Please validate the highlighted fields', { type: 'error', position: 'bottom-left' });
      } else {
        toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
      }
    } finally {
      setSaving(false);
    }
  };

  const drawerTitle = (
    <div className="note-drawer__header-inner">
      <span className="note-drawer__title">{isEdit ? 'Edit Note' : 'Add Note'}</span>
      <button className="note-drawer__close-btn" onClick={onClose} type="button">
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title={drawerTitle}
      width={720}
      closable={false}
      destroyOnClose
      className="note-drawer"
      footer={(
        <div className="note-drawer-footer">
          <TertiaryButton title="Cancel" onClick={onClose} disabled={saving || deleting} />
          <PrimaryButton
            title={isEdit ? 'Save' : 'Add'}
            iconPosition="start"
            onClick={handleSave}
            disabled={saving || deleting}
          >
            {saving && (
              <CircularProgress size={16} thickness={5} style={{ color: 'inherit' }} />
            )}
          </PrimaryButton>
        </div>
      )}
    >
      <div className="note-drawer-form">
        <div className="note-drawer-type-field">
          <Dropdown
            label="Type"
            required
            enums={reviewerNoteType}
            value={formik.values.type}
            onChange={(opt) => {
              formik.setFieldValue('type', opt);
              if (opt.value !== '') formik.setFieldError('type', undefined);
            }}
            error={formik.errors.type as string}
            showIcons={false}
          />
        </div>
        <TextInput
          label="Title"
          required
          value={formik.values.title}
          onChange={(e) => formik.setFieldValue('title', e.target.value)}
          error={formik.errors.title}
          maxLength={250}
        />
        {showComment && (
          <TextArea
            label="Comment"
            required
            value={formik.values.comment}
            onChange={(e) => formik.setFieldValue('comment', e.target.value)}
            error={formik.errors.comment}
            rows={10}
          />
        )}
        {isEdit && canDelete && (
          <>
            <div className="note-drawer-divider" />
            <button
              type="button"
              className="note-drawer-delete-btn"
              onClick={onDelete}
              disabled={saving || deleting}
            >
              <CloseIcon />
              {deleting ? 'Deleting...' : 'Delete Note'}
            </button>
          </>
        )}
      </div>
    </Drawer>
  );
};
